import { useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Calendar,
  ExternalLink,
  MapPin,
  Pencil,
  QrCode,
  Smartphone,
  Play,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { EntityTypeBadge } from '@/components/ui/EntityTypeBadge';
import { StationAccessTokenButton } from './StationAccessTokenButton';
import { OperatorEditToggle } from './OperatorEditToggle';
import { StationQrDialog } from './StationQrDialog';
import { useT } from '@/lib/i18n/useT';
import type { XkStationSummary } from '@/lib/api/types';

interface StationHeaderProps {
  summary: XkStationSummary;
  onEdit: () => void;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function StationHeader({ summary, onEdit }: StationHeaderProps) {
  const t = useT();
  const [qrOpen, setQrOpen] = useState(false);
  const stationId = summary.station.id;
  const { event, survey, qr } = summary;
  const eventStart = formatDate(event?.startDate ?? null);
  const eventEnd = formatDate(event?.endDate ?? null);

  // Where the game name goes: the live consumer preview, i.e. what a guest
  // actually sees when they scan this station. `preview=1` runs it dry so no
  // stock is touched. It used to open a read-only list of journey steps.
  const livePreviewUrl = survey
    ? `/public-surveys?surveyId=${survey.id}&stationId=${stationId}&preview=1`
    : null;
  // The matching "edit" affordance — Design Journey on the parent event.
  const designJourneyUrl = event
    ? `/projects/${event.eventGroupId}/events/${event.id}?tab=survey`
    : null;

  return (
    <div className="space-y-4">
      {/* No Back button: the breadcrumbs below carry the way out, and this
          page is reached from a real place in the hierarchy. */}

      {/* Caso 16 — navigation breadcrumbs across the hierarchy. */}
      {event && (
        <Breadcrumbs
          items={[
            {
              label: t('breadcrumbs.project'),
              to: `/projects/${event.eventGroupId}`,
            },
            {
              label: event.name,
              to: `/projects/${event.eventGroupId}/events/${event.id}`,
            },
            { label: summary.station.name },
          ]}
        />
      )}

      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <EntityTypeBadge type="station" />
            <h1 className="text-3xl font-bold">{summary.station.name}</h1>
            {summary.station.status && summary.station.status !== 'Enable' && (
              <Badge tone="warn">{summary.station.status}</Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--color-fg-muted)]">
            {event ? (
              <Link
                to={`/projects/${event.eventGroupId}/events/${event.id}`}
                className="inline-flex items-center gap-1 hover:text-[var(--color-fg-primary)]"
              >
                <MapPin className="h-3.5 w-3.5" />
                {event.name}
                {event.codeRef && (
                  <span className="text-[var(--color-fg-subtle)]">
                    · {event.codeRef}
                  </span>
                )}
              </Link>
            ) : (
              <span>
                {t('stations.detail.eventLabel')} #{summary.station.eventId}
              </span>
            )}

            {(eventStart || eventEnd) && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {eventStart}
                {eventEnd && eventEnd !== eventStart ? ` → ${eventEnd}` : ''}
              </span>
            )}

            {survey && livePreviewUrl ? (
              <span className="inline-flex items-center gap-2">
                <a
                  href={livePreviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={t('stations.detail.playGameHint')}
                  className="inline-flex items-center gap-1 text-[var(--color-accent-ink)] hover:underline"
                >
                  <Play className="h-3.5 w-3.5" />
                  {survey.name}
                  <ExternalLink className="h-3 w-3" />
                </a>
                {designJourneyUrl && (
                  <Link
                    to={designJourneyUrl}
                    title={t('stations.detail.editJourneyHint')}
                    className="inline-flex items-center gap-1 text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
                  >
                    <Pencil className="h-3 w-3" />
                    {t('common.edit')}
                  </Link>
                )}
              </span>
            ) : (
              <span className="text-[var(--color-fg-subtle)]">
                {t('stations.detail.noSurveyAssigned')}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {qr.shortUrl && (
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              aria-label={t('stations.detail.viewQr')}
              className="grid h-24 w-24 place-items-center rounded-xl bg-white p-2 ring-1 ring-[var(--color-border-default)] transition hover:ring-[var(--color-accent)]"
            >
              <QRCodeSVG value={qr.shortUrl} size={80} level="M" />
            </button>
          )}
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              leftIcon={<Pencil className="h-3.5 w-3.5" />}
              onClick={onEdit}
            >
              {t('stations.detail.edit')}
            </Button>
            <Button
              variant="ghost"
              leftIcon={<QrCode className="h-3.5 w-3.5" />}
              onClick={() => setQrOpen(true)}
            >
              {t('stations.detail.viewQr')}
            </Button>
            {/* Caso 11 — booth-operator's mobile-first view of this station. */}
            <Link to={`/operator/${stationId}`}>
              <Button
                variant="ghost"
                leftIcon={<Smartphone className="h-3.5 w-3.5" />}
              >
                {t('stations.detail.operatorMode')}
              </Button>
            </Link>
            {/* Caso 11 v3 — Mint a shareable operator-mode URL bound to a
                station access token. Auth exchange (token → session) is
                the v4 follow-up; the URL is the handle for now. */}
            <StationAccessTokenButton stationId={stationId} />
          </div>
        </div>
        {/* 094 — qué puede tocar el operador desde su teléfono, y con qué código. */}
        <div className="mt-3">
          <OperatorEditToggle stationId={stationId} />
        </div>
      </header>

      <StationQrDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        stationId={stationId}
        stationName={summary.station.name}
      />
    </div>
  );
}
